import { Controller } from "@hotwired/stimulus"

// Module-level variable holds the previous URL across page transitions.
let previousUrl = window.location.href

// Connects to data-controller="page-transition"
export default class extends Controller {
  connect() {
    // Bind the event listener methods so they can be removed on disconnect.
    this.handleBeforeVisit = this.#handleBeforeVisit.bind(this)
    this.handleTurboLoad = this.#handleTurboLoad.bind(this)

    // Listen to Turbo navigation events.
    document.addEventListener("turbo:before-visit", this.handleBeforeVisit)
    document.addEventListener("turbo:load", this.handleTurboLoad)
  }

  disconnect() {
    // Remove event listeners to prevent memory leaks.
    document.removeEventListener("turbo:before-visit", this.handleBeforeVisit)
    document.removeEventListener("turbo:load", this.handleTurboLoad)
  }

  /**
   * Returns the group for a given path.
   * If the path contains "/settings", it's in the "settings" group;
   * otherwise, it's considered part of the "app" pages.
   */
  #getGroup(path) {
    if (path.includes("/settings") || path.includes("/billing")) return "settings"
    return "app"
  }

  /**
   * Handles the turbo:before-visit event.
   * If navigating between different groups ("app" vs. "settings"),
   * the method adds the "page-transition-out" class.
   */
  #handleBeforeVisit(event) {
    const targetUrl = event.detail.url
    try {
      // Parse target and current URLs.
      const nextPath = new URL(targetUrl, window.location.origin).pathname
      const previousPath = new URL(previousUrl, window.location.origin).pathname

      // Determine the page groups.
      const previousGroup = this.#getGroup(previousPath)
      const nextGroup = this.#getGroup(nextPath)

      // Trigger the transition only if the page groups differ.
      if (previousGroup !== nextGroup) {
        document.body.classList.add("page-transition-out")
      }
    } catch (error) {
      console.error("Error parsing URLs:", error)
    }
  }

  /**
   * Handles the turbo:load event.
   * Updates the stored URL and manages the transition-in effect if the group has changed.
   */
  #handleTurboLoad() {
    const currentUrl = window.location.href
    try {
      const previousPath = new URL(previousUrl, window.location.origin).pathname
      const currentPath = new URL(currentUrl, window.location.origin).pathname
      const previousGroup = this.#getGroup(previousPath)
      const currentGroup = this.#getGroup(currentPath)

      if (previousGroup !== currentGroup) {
        document.body.classList.remove("page-transition-out")
        document.body.classList.add("page-transition-in")
        // Remove the transition-in class after the animation duration.
        setTimeout(() => {
          document.body.classList.remove("page-transition-in")
        }, 300)
      }
    } catch (error) {
      console.error("Error parsing URLs during turbo:load:", error)
    }
    // Update the module-level previousUrl for subsequent navigations.
    previousUrl = currentUrl
  }
}
